import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useLang } from "@/context/LanguageContext";

const CurrencyContext = createContext(null);

const DEFAULT_RATES = {
  AED: 1,
  USD: 0.2722,
  GBP: 0.2138,
  INR: 23.68,
  EUR: 0.2495,
};

const SYMBOLS = { AED: "AED", USD: "$", GBP: "£", INR: "₹", EUR: "€" };
const SYMBOLS_AR = { AED: "درهم", USD: "$", GBP: "£", INR: "₹", EUR: "€" };

export const CurrencyProvider = ({ children }) => {
  // Read the active language directly from LanguageContext (guaranteed to be
  // above us in the App tree). Previously we sniffed localStorage via a
  // helper, which caused stale reads on the very first render after a
  // language toggle — the LanguageContext state had flipped to "en" but
  // localStorage had not yet been re-written by its useEffect, so
  // `format()` returned Arabic-Indic digits inside an English page. That's
  // the "mixed English + Arabic numbers" bug reported on production.
  const { lang } = useLang();
  const isAR = lang === "ar";
  const [currency, setCurrency] = useState(() => localStorage.getItem("propertic_currency") || "AED");
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    localStorage.setItem("propertic_currency", currency);
  }, [currency]);

  // Fetch live rates once on mount (free tier , no key required).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("https://open.er-api.com/v6/latest/AED", { cache: "no-store" });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled || !data?.rates) return;
        setRates({
          AED: 1,
          USD: data.rates.USD || DEFAULT_RATES.USD,
          GBP: data.rates.GBP || DEFAULT_RATES.GBP,
          INR: data.rates.INR || DEFAULT_RATES.INR,
          EUR: data.rates.EUR || DEFAULT_RATES.EUR,
        });
        setUpdatedAt(data.time_last_update_utc || new Date().toISOString());
      } catch {
        // fall back to defaults silently
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const convert = useCallback(
    (amountAed) => {
      const rate = rates[currency] ?? 1;
      return amountAed * rate;
    },
    [rates, currency]
  );

  const format = useCallback(
    (amountAed, opts = {}) => {
      const { compact = false, decimals } = opts;
      const converted = convert(amountAed);
      const symbol = (isAR ? SYMBOLS_AR : SYMBOLS)[currency] || currency;
      const absVal = Math.abs(converted);
      // Locale for number rendering. `ar-EG` produces Eastern Arabic-Indic
      // digits (٠-٩) with the Arabic thousands separator (٬) and Arabic
      // decimal separator (٫). `en-US` keeps Latin digits + Latin comma.
      const locale = isAR ? "ar-EG" : "en-US";
      let display;
      if (compact) {
        const compactNum = (val, d) =>
          new Intl.NumberFormat(locale, {
            minimumFractionDigits: d,
            maximumFractionDigits: d,
          }).format(val);
        if (absVal >= 1e9) display = `${compactNum(converted / 1e9, decimals ?? 1)}${isAR ? " مليار" : "B"}`;
        else if (absVal >= 1e6) display = `${compactNum(converted / 1e6, decimals ?? 1)}${isAR ? " مليون" : "M"}`;
        else if (absVal >= 1e3) display = `${compactNum(converted / 1e3, decimals ?? 1)}${isAR ? " ألف" : "K"}`;
        else display = compactNum(converted, decimals ?? 0);
      } else {
        display = new Intl.NumberFormat(locale, {
          maximumFractionDigits: decimals ?? 0,
        }).format(converted);
      }
      if (isAR) display = display.replace(/\u066C/g, ",");
      return isAR && currency === "AED" ? `${display} ${symbol}` : `${symbol} ${display}`;
    },
    [convert, currency, isAR]
  );

  // Convenience: format an AED-billions figure (e.g. 355.87) into the
  // active currency with a "B/M/K" or Arabic "مليار/مليون/ألف" suffix.
  const formatBn = useCallback(
    (aedBn, opts = {}) => {
      const amountAed = Number(aedBn || 0) * 1e9;
      return format(amountAed, { compact: true, decimals: opts.decimals ?? 2 });
    },
    [format]
  );

  const value = useMemo(
    () => ({ currency, setCurrency, rates, updatedAt, convert, format, formatBn, symbols: SYMBOLS }),
    [currency, rates, updatedAt, convert, format, formatBn]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used inside CurrencyProvider");
  return ctx;
};
